import { useEffect, useState } from "react";

import {
  createKeycloakTeam,
  deleteKeycloakTeam,
  diagnoseKeycloakAdminAccess,
  fetchKeycloakTeams,
  fetchKeycloakUsersForManagement,
  updateKeycloakTeam,
} from "./api";

import Swal from "sweetalert2";

const EMPTY_FORM = {
  team: "",
  team_name: "",
  department: "",
  location: "",
};

export default function TeamsView() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingTeamId, setDeletingTeamId] = useState("");
  const [editingTeamId, setEditingTeamId] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  async function loadTeams() {
    setLoading(true);
    setError("");

    try {
      const adminAccess = await diagnoseKeycloakAdminAccess();
      if (!adminAccess.ok) {
        throw new Error(adminAccess.message);
      }

      const [teams, users] = await Promise.all([
        fetchKeycloakTeams(),
        fetchKeycloakUsersForManagement(),
      ]);

      const usersByTeam = {};
      users.forEach((user) => {
        (user.team || []).forEach((teamName) => {
          if (!usersByTeam[teamName]) {
            usersByTeam[teamName] = [];
          }
          usersByTeam[teamName].push(user);
        });
      });

      const data = teams.map((team) => {
        const teamUsers = usersByTeam[team.team] || [];
        return {
          ...team,
          processors: teamUsers
            .filter((user) => (user.role || []).includes("Processor"))
            .map((user) => user.first_name || user.email),
          uploaders: teamUsers
            .filter((user) => (user.role || []).includes("Uploader"))
            .map((user) => user.first_name || user.email),
        };
      });

      setRows(data);
    } catch (requestError) {
      setError(requestError.message || "Unable to load teams.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTeams();
  }, []);

  function updateField(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    setEditingTeamId("");
  }

  function handleEdit(row) {
    setError("");
    setStatus("");
    setEditingTeamId(row.id || "");
    setForm({
      team: row.team || "",
      team_name: row.team_name || "",
      department: row.department || "",
      location: row.location || "",
    });
  }

  async function handleDelete(row) {
    if (!row?.id) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to delete team: missing team id.",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Delete Team?",
      text: `Are you sure you want to delete team "${row.team}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    setDeletingTeamId(row.id);
    setError("");

    try {
      await deleteKeycloakTeam(row.id);

      if (editingTeamId === row.id) {
        resetForm();
      }

      await loadTeams();

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Team deleted successfully.",
        confirmButtonText: "OK",
      });

    } catch (requestError) {

      await Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text:
          requestError.message ||
          "Unable to delete team.",
        confirmButtonText: "OK",
      });

      setError(
        requestError.message ||
        "Unable to delete team."
      );

    } finally {
      setDeletingTeamId("");
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      const payload = {
        team: form.team,
        team_name: form.team_name.trim(),
        department: form.department.trim(),
        location: form.location.trim(),
      };

      if (editingTeamId) {
        await updateKeycloakTeam(editingTeamId, payload);
      } else {
        await createKeycloakTeam(payload);
      }

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: editingTeamId
          ? "Team updated successfully."
          : "Team added successfully.",
        confirmButtonText: "OK",
      });

      resetForm();
      await loadTeams();

    } catch (requestError) {

      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          requestError.message ||
          `Unable to ${editingTeamId ? "update" : "add"} team.`,
        confirmButtonText: "OK",
      });

      setError(
        requestError.message ||
        `Unable to ${editingTeamId ? "update" : "add"} team.`
      );

    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="teams-view">
      <div className="workspace-panel teams-view__hero">
        <div>
          <p className="eyebrow">Teams</p>
          <h2>Team Management</h2>
          <p className="config-view__intro">
            Manage teams directly in Keycloak groups from this dashboard, including create, update, and delete.
          </p>
        </div>
        <div className="teams-view__summary">
          <span className="teams-view__summary-count">{rows.length}</span>
          <span className="teams-view__summary-label">teams configured</span>
        </div>
      </div>

      {status ? <div className="config-banner config-banner--success">{status}</div> : null}
      {error ? <div className="config-banner config-banner--error">{error}</div> : null}

      <div className="teams-layout">
        <form className="workspace-panel teams-form" onSubmit={handleSubmit}>
          <div className="users-form__header">
            <div>
              <p className="eyebrow">{editingTeamId ? "Edit Team" : "Create Team"}</p>
              <h2>{editingTeamId ? "Update team group" : "Add a new team group"}</h2>
            </div>
            {editingTeamId ? (
              <button className="ghost-button" type="button" onClick={resetForm}>
                Cancel Edit
              </button>
            ) : null}
          </div>

          <label className="config-field">
            <span>Team Code</span>
            <input
              type="text"
              value={form.team}
              onChange={(event) => updateField("team", event.target.value)}
              required
              placeholder="e.g. Kolkata"
            />
          </label>

          <label className="config-field">
            <span>Team Name</span>
            <input
              type="text"
              value={form.team_name}
              onChange={(event) => updateField("team_name", event.target.value)}
              placeholder="e.g. Accounts Payable West"
              required
            />
          </label>

          <label className="config-field">
            <span>Department Name</span>
            <input
              type="text"
              value={form.department}
              onChange={(event) => updateField("department", event.target.value)}
              placeholder="Optional"
            />
          </label>

          <label className="config-field">
            <span>Location</span>
            <input
              type="text"
              value={form.location}
              onChange={(event) => updateField("location", event.target.value)}
              placeholder="Optional"
            />
          </label>

          <button className="primary-button" type="submit" disabled={saving}>
            {saving ? (editingTeamId ? "Updating..." : "Adding...") : editingTeamId ? "Update Team" : "Add Team"}
          </button>
        </form>

        <div className="workspace-panel">
          <div className="teams-table__header">
            <div>
              <p className="eyebrow">Directory</p>
              <h2>All Teams</h2>
            </div>
          </div>

          {loading ? (
            <p className="config-note">Loading existing teams…</p>
          ) : (
            <div className="config-table">
              <table>
                <thead>
                  <tr>
                    <th>Team</th>
                    <th>Team Name</th>
                    <th>Department</th>
                    <th>Location</th>
                    <th>Processors</th>
                    <th>Uploaders</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length ? (
                    rows.map((row) => (
                      <tr key={row.id || row.team}>
                        <td>{row.team}</td>
                        <td>{row.team_name}</td>
                        <td>{row.department || "-"}</td>
                        <td>{row.location || "-"}</td>
                        <td>{(row.processors || []).join(", ") || "-"}</td>
                        <td>{(row.uploaders || []).join(", ") || "-"}</td>
                        <td>
                          <button className="ghost-button" type="button" onClick={() => handleEdit(row)}>
                            Edit
                          </button>
                          <button
                            className="ghost-button"
                            type="button"
                            onClick={() => handleDelete(row)}
                            disabled={deletingTeamId === row.id}
                          >
                            {deletingTeamId === row.id ? "Deleting..." : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7">No teams found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
